import React, { useState, useEffect } from 'react';
import { 
    List, 
    useListContext,
    useDataProvider,
    Edit,
    SimpleForm,
    useRecordContext,
    useNotify,
    useRedirect
} from 'react-admin';
import { 
    Box, 
    Typography, 
    Paper, 
    Dialog, 
    DialogTitle, 
    DialogContent, 
    DialogActions, 
    Button,
    Chip,
    Autocomplete,
    TextField as MuiTextField,
    InputAdornment,
    ButtonBase,
    Skeleton,
    FormControlLabel,
    Switch
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import LoopIcon from '@mui/icons-material/Loop';
import PetsOutlinedIcon from '@mui/icons-material/PetsOutlined';
import RouteOutlinedIcon from '@mui/icons-material/RouteOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import SearchIcon from '@mui/icons-material/Search';
import { uploadRouteImage } from './s3Service';
import DetailLoadingState from './components/DetailLoadingState';
import { ImageCropDialog } from './components/ImageCropDialog';
import RouteMapPreview from './components/RouteMapPreview';

let routeAnimalsNamesCache = null;
const APP_CARD_WIDTH = 361;
const APP_CARD_HEIGHT = 145;
const ROUTE_CARD_ASPECT = APP_CARD_WIDTH / APP_CARD_HEIGHT;
const ROUTE_EXPORT_WIDTH = APP_CARD_WIDTH * 5;
const ROUTE_EXPORT_HEIGHT = APP_CARD_HEIGHT * 5;

const isValidRouteImage = (url) => Boolean(
    typeof url === 'string'
    && /^https?:\/\//i.test(url)
    && !url.startsWith('http://example.com')
);

const getRouteCountLabel = (count) => {
    const mod10 = count % 10;
    const mod100 = count % 100;
    if (mod10 === 1 && mod100 !== 11) return `${count} маршрут`;
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return `${count} маршрута`;
    return `${count} маршрутов`;
};

const RouteSearchInput = () => {
    const { filterValues, setFilters } = useListContext();
    const activeNameFilter = filterValues?.name || '';
    const [value, setValue] = useState(activeNameFilter);

    useEffect(() => {
        setValue(activeNameFilter);
    }, [activeNameFilter]);

    useEffect(() => {
        const timeoutId = window.setTimeout(() => {
            if (value === activeNameFilter) return;
            setFilters(value.trim() ? { name: value } : {});
        }, 300);

        return () => window.clearTimeout(timeoutId);
    }, [activeNameFilter, setFilters, value]);

    return (
        <MuiTextField
            value={value}
            onChange={(event) => setValue(event.target.value)}
            placeholder="Найти маршрут"
            aria-label="Поиск маршрута"
            size="small"
            className="routes-search"
            InputProps={{
                startAdornment: (
                    <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
                ),
            }}
        />
    );
};

const RouteCard = ({ record, animalsMap }) => {
    const redirect = useRedirect();
    const animalIds = Array.isArray(record.animals_id) ? record.animals_id : [];
    const visibleAnimals = animalIds.slice(0, 2);

    return (
        <ButtonBase
            component="article"
            className="route-card"
            onClick={() => redirect('edit', 'routes', record.id)}
            aria-label={`Открыть маршрут ${record.name || record.id}`}
        >
            <Box className="route-card-image">
                {isValidRouteImage(record.image_url) ? (
                    <Box component="img" src={record.image_url} alt="" loading="lazy" />
                ) : (
                    <Box className="route-card-image-empty">
                        <ImageOutlinedIcon />
                        <Typography>Обложка не добавлена</Typography>
                    </Box>
                )}
                <Chip size="small" label={`ID ${record.id}`} className="route-card-id" />
                {record.is_circular && (
                    <Chip size="small" icon={<LoopIcon />} label="Кольцевой" className="route-card-circular" />
                )}
                {(record.duration || record.distance) && (
                    <Chip
                        size="small"
                        icon={<AccessTimeOutlinedIcon />}
                        label={[record.duration, record.distance].filter(Boolean).join(' · ')}
                        className="route-card-duration"
                    />
                )}
            </Box>

            <Box className="route-card-content">
                <Box className="route-card-title-row">
                    <Box sx={{ minWidth: 0 }}>
                        <Typography component="h2" className="route-card-title" noWrap>
                            {record.name || 'Маршрут без названия'}
                        </Typography>
                        <Typography className="route-card-description">
                            {record.description || 'Описание маршрута пока не добавлено'}
                        </Typography>
                    </Box>
                    <ChevronRightIcon className="route-card-arrow" aria-hidden="true" />
                </Box>

                <Box className="route-card-animals">
                    <Box className="route-card-animals-label">
                        <PetsOutlinedIcon />
                        <Typography component="span">{animalIds.length} животных</Typography>
                    </Box>
                    <Box className="route-card-animal-chips">
                        {visibleAnimals.map((animalId) => (
                            <Chip key={animalId} size="small" label={animalsMap[animalId] || `Животное #${animalId}`} />
                        ))}
                        {animalIds.length > visibleAnimals.length && (
                            <Chip size="small" label={`+${animalIds.length - visibleAnimals.length}`} className="route-card-more-chip" />
                        )}
                    </Box>
                </Box>
            </Box>
        </ButtonBase>
    );
};

const RouteCards = () => {
    const { data = [], isPending } = useListContext();
    const [animalsMap, setAnimalsMap] = useState(() => routeAnimalsNamesCache || {});
    const [isResolvingAnimals, setIsResolvingAnimals] = useState(() => !routeAnimalsNamesCache);
    const dataProvider = useDataProvider();

    // Загружаем всех животных для сопоставления ID с названиями
    useEffect(() => {
        if (routeAnimalsNamesCache) {
            setAnimalsMap(routeAnimalsNamesCache);
            setIsResolvingAnimals(false);
            return;
        }

        setIsResolvingAnimals(true);
        dataProvider.getList('animals', {
            pagination: { page: 1, perPage: 1000 },
            sort: { field: 'id', order: 'ASC' },
            filter: {},
        }).then(({ data }) => {
            const map = {};
            data.forEach(animal => {
                map[animal.id] = animal.name;
            });
            routeAnimalsNamesCache = map;
            setAnimalsMap(map);
        }).catch(error => {
            console.error('Error loading animals:', error);
        }).finally(() => {
            setIsResolvingAnimals(false);
        });
    }, [dataProvider]);

    const hasRoutesWithAnimals = Array.isArray(data)
        && data.some((record) => Array.isArray(record?.animals_id) && record.animals_id.length > 0);

    if (isPending || (hasRoutesWithAnimals && isResolvingAnimals)) {
        return (
            <Box className="routes-card-grid" aria-label="Загрузка маршрутов">
                {[0, 1, 2, 3].map((item) => (
                    <Box className="route-card route-card-skeleton" key={item}>
                        <Skeleton variant="rectangular" className="route-card-skeleton-image" />
                        <Box className="route-card-content">
                            <Skeleton width="58%" height={28} />
                            <Skeleton width="92%" />
                            <Skeleton width="74%" />
                        </Box>
                    </Box>
                ))}
            </Box>
        );
    }

    return (
        <Box className="routes-card-grid">
            {data.map((record) => <RouteCard key={record.id} record={record} animalsMap={animalsMap} />)}
        </Box>
    );
};

const RouteListHeader = () => {
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const { total = 0 } = useListContext();

    return (
        <>
            <Box className="routes-page-header">
                <Box>
                    <Box className="routes-page-title-row">
                        <Typography variant="h4" component="h1">Маршруты</Typography>
                        <Chip size="small" label={getRouteCountLabel(total)} className="routes-total-chip" />
                    </Box>
                    <Typography color="text.secondary" className="routes-page-subtitle">
                        Тематические прогулки по зоопарку и их состав
                    </Typography>
                </Box>
                <Box className="routes-page-actions">
                    <RouteSearchInput />
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateDialogOpen(true)}>
                        Создать маршрут
                    </Button>
                </Box>
            </Box>
            <CreateRouteDialog 
                open={createDialogOpen} 
                onClose={() => setCreateDialogOpen(false)} 
            />
        </>
    );
};

// Диалог создания маршрута
const CreateRouteDialog = ({ open, onClose }) => {
    const [routeName, setRouteName] = useState('');
    const [duration, setDuration] = useState('');
    const [distance, setDistance] = useState('');
    const [isCircular, setIsCircular] = useState(true);
    const [finishDescription, setFinishDescription] = useState('');
    const [description, setDescription] = useState('');
    const [routeImageFile, setRouteImageFile] = useState(null);
    const [cropSourceFile, setCropSourceFile] = useState(null);
    const [cropDialogOpen, setCropDialogOpen] = useState(false);
    const [selectedAnimals, setSelectedAnimals] = useState([]);
    const [animals, setAnimals] = useState([]);
    const [uploading, setUploading] = useState(false);
    
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const redirect = useRedirect();

    // Загружаем всех животных при открытии диалога
    useEffect(() => {
        if (open) {
            dataProvider.getList('animals', {
                pagination: { page: 1, perPage: 1000 },
                sort: { field: 'name', order: 'ASC' },
                filter: {}
            }).then(({ data }) => {
                setAnimals(data);
            }).catch(error => {
                console.error('Error loading animals:', error);
                notify('Ошибка загрузки животных', { type: 'error' });
            });
        }
    }, [open, dataProvider, notify]);

    const handleCreate = async () => {
        if (!routeName) {
            notify('Название обязательно', { type: 'warning' });
            return;
        }

        if (selectedAnimals.length === 0) {
            notify('Выберите хотя бы одно животное', { type: 'warning' });
            return;
        }

        setUploading(true);

        try {
            const result = await dataProvider.create('routes', {
                data: {
                    name: routeName,
                    duration: duration || '',
                    distance: distance || '',
                    is_circular: isCircular,
                    finish_description: finishDescription || '',
                    description: description || '',
                    image_url: '',
                    animals_id: selectedAnimals.map(a => a.id)
                }
            });

            if (routeImageFile) {
                try {
                    const imageUrl = await uploadRouteImage(routeImageFile, result.data.id);

                    await dataProvider.update('routes', {
                        id: result.data.id,
                        data: {
                            name: routeName,
                            duration: duration || '',
                            distance: distance || '',
                            is_circular: isCircular,
                            finish_description: finishDescription || '',
                            description: description || '',
                            image_url: imageUrl,
                            animals_id: selectedAnimals.map(a => a.id)
                        },
                        previousData: result.data
                    });
                } catch (uploadError) {
                    console.error('Error uploading route image:', uploadError);
                    notify(`Маршрут создан, но картинка не загрузилась: ${uploadError.message}`, { type: 'warning' });
                }
            }
            
            notify('Маршрут успешно создан', { type: 'success' });
            handleClose();
            redirect('edit', 'routes', result.data.id);
        } catch (error) {
            console.error('Error creating route:', error);
            notify(`Ошибка при создании: ${error.message}`, { type: 'error' });
        } finally {
            setUploading(false);
        }
    };

    const handleRouteImageChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setCropSourceFile(file);
        setCropDialogOpen(true);
        e.target.value = '';
    };

    const handleCropCancel = () => {
        setCropDialogOpen(false);
        setCropSourceFile(null);
    };

    const handleCropApply = (croppedFile, error = null) => {
        if (error) {
            notify(`Ошибка обрезки: ${error.message}`, { type: 'error' });
            handleCropCancel();
            return;
        }

        if (croppedFile) {
            setRouteImageFile(croppedFile);
            notify(`Файл "${croppedFile.name}" готов к загрузке`, { type: 'info' });
        }
        handleCropCancel();
    };

    const handleClose = () => {
        setRouteName('');
        setDuration('');
        setDistance('');
        setIsCircular(true);
        setFinishDescription('');
        setDescription('');
        setRouteImageFile(null);
        setCropSourceFile(null);
        setCropDialogOpen(false);
        setSelectedAnimals([]);
        onClose();
    };

    return (
        <Dialog className="route-create-dialog" open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle>
                <Box className="route-create-title">
                    <Box className="route-create-title-icon"><RouteOutlinedIcon /></Box>
                    <Box>
                        <Typography component="h2">Новый маршрут</Typography>
                        <Typography>Создайте основу — детали можно будет дополнить в редакторе</Typography>
                    </Box>
                </Box>
            </DialogTitle>
            <DialogContent className="route-create-content">
                <Box className="route-create-fields">
                    <MuiTextField
                        label="Название маршрута *"
                        value={routeName}
                        onChange={(e) => setRouteName(e.target.value)}
                        fullWidth
                        required
                    />

                    <MuiTextField
                        label="Время прохождения"
                        value={duration}
                        onChange={(e) => setDuration(e.target.value)}
                        fullWidth
                        placeholder="Например: 40 минут"
                    />

                    <MuiTextField
                        label="Пройденное расстояние"
                        value={distance}
                        onChange={(e) => setDistance(e.target.value)}
                        fullWidth
                        placeholder="Например: 1,6 км"
                    />

                    <FormControlLabel
                        control={<Switch checked={isCircular} onChange={(e) => setIsCircular(e.target.checked)} />}
                        label="Кольцевой маршрут — возвращает посетителя в зону входа"
                    />

                    {isCircular && (
                        <MuiTextField
                            label="Где заканчивается маршрут"
                            value={finishDescription}
                            onChange={(e) => setFinishDescription(e.target.value)}
                            fullWidth
                            placeholder="Например: у пингвинов, около 80 м до главного входа"
                        />
                    )}

                    <MuiTextField
                        label="Описание"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        fullWidth
                        multiline
                        minRows={3}
                        placeholder="Описание тематического маршрута"
                    />

                    <Box className="route-create-upload">
                        <Button
                            variant="outlined"
                            component="label"
                            fullWidth
                            disabled={uploading}
                            startIcon={<ImageOutlinedIcon />}
                        >
                            {uploading ? 'Загрузка...' : 'Выбрать и обрезать картинку маршрута'}
                            <input
                                type="file"
                                hidden
                                accept="image/*"
                                onChange={handleRouteImageChange}
                                disabled={uploading}
                            />
                        </Button>
                        {routeImageFile && (
                            <Typography variant="caption" color="primary" sx={{ display: 'block', mt: 1 }}>
                                Выбран файл: {routeImageFile.name}
                            </Typography>
                        )}
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                            Формат карточки приложения — 361×145. Изображение можно обрезать перед загрузкой.
                        </Typography>
                    </Box>

                    <Autocomplete
                        multiple
                        options={animals}
                        getOptionLabel={(option) => `${option.id} - ${option.name}`}
                        value={selectedAnimals}
                        onChange={(event, newValue) => {
                            setSelectedAnimals(newValue);
                        }}
                        renderInput={(params) => (
                            <MuiTextField
                                {...params}
                                label="Выберите животных *"
                                placeholder="Начните вводить название или ID"
                            />
                        )}
                        renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                                <Chip
                                    label={`${option.id} - ${option.name}`}
                                    {...getTagProps({ index })}
                                    key={option.id}
                                />
                            ))
                        }
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>Отмена</Button>
                <Button 
                    onClick={handleCreate} 
                    variant="contained" 
                    disabled={!routeName || selectedAnimals.length === 0 || uploading}
                >
                    {uploading ? 'Создание...' : 'Создать'}
                </Button>
            </DialogActions>
            <ImageCropDialog
                open={cropDialogOpen}
                file={cropSourceFile}
                title="Обрезка картинки маршрута"
                helperText="Размер контейнера в приложении: 361x145. Итог загружается с запасом качества."
                aspectRatio={ROUTE_CARD_ASPECT}
                outputWidth={ROUTE_EXPORT_WIDTH}
                outputHeight={ROUTE_EXPORT_HEIGHT}
                onCancel={handleCropCancel}
                onApply={handleCropApply}
            />
        </Dialog>
    );
};

// Пустой компонент для отображения когда нет данных
const EmptyRouteList = () => {
    const [createDialogOpen, setCreateDialogOpen] = useState(false);

    return (
        <>
            <Box className="routes-empty-state">
                <Box className="routes-empty-icon"><RouteOutlinedIcon /></Box>
                <Typography component="h1">Маршрутов пока нет</Typography>
                <Typography>Создайте первую тематическую прогулку и добавьте в неё животных</Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setCreateDialogOpen(true)}
                >
                    Создать маршрут
                </Button>
            </Box>
            <CreateRouteDialog 
                open={createDialogOpen} 
                onClose={() => setCreateDialogOpen(false)} 
            />
        </>
    );
};

// Компонент списка маршрутов
export const RouteList = (props) => (
    <List 
        {...props}
        exporter={false}
        title="Тематические маршруты"
        perPage={25}
        sort={{ field: 'id', order: 'ASC' }}
        actions={false}
        empty={<EmptyRouteList />}
        className="routes-list"
    >
        <Box className="routes-list-page">
            <RouteListHeader />
            <RouteCards />
        </Box>
    </List>
);

// Контент формы редактирования
const RouteEditContent = () => {
    const record = useRecordContext();
    const [routeName, setRouteName] = useState('');
    const [duration, setDuration] = useState('');
    const [distance, setDistance] = useState('');
    const [isCircular, setIsCircular] = useState(false);
    const [finishDescription, setFinishDescription] = useState('');
    const [description, setDescription] = useState('');
    const [imageUrl, setImageUrl] = useState('');
    const [routeImageFile, setRouteImageFile] = useState(null);
    const [cropSourceFile, setCropSourceFile] = useState(null);
    const [cropDialogOpen, setCropDialogOpen] = useState(false);
    const [animals, setAnimals] = useState([]);
    const [selectedAnimals, setSelectedAnimals] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [animalsLoading, setAnimalsLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [imagePreviewFailed, setImagePreviewFailed] = useState(false);
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const redirect = useRedirect();

    useEffect(() => {
        if (record) {
            setRouteName(record.name || '');
            setDuration(record.duration || '');
            setDistance(record.distance || '');
            setIsCircular(Boolean(record.is_circular));
            setFinishDescription(record.finish_description || '');
            setDescription(record.description || '');
            setImageUrl(record.image_url || '');
        }
    }, [record]);

    // Загружаем всех животных
    useEffect(() => {
        setAnimalsLoading(true);
        dataProvider.getList('animals', {
            pagination: { page: 1, perPage: 1000 },
            sort: { field: 'name', order: 'ASC' },
            filter: {}
        }).then(({ data }) => {
            setAnimals(data);
            
            // Создаем карту ID -> животное
            const map = {};
            data.forEach(animal => {
                map[animal.id] = animal;
            });

            // Устанавливаем выбранных животных на основе record
            if (record && record.animals_id) {
                const selected = record.animals_id
                    .map(id => map[id])
                    .filter(animal => animal !== undefined);
                setSelectedAnimals(selected);
            }
        }).catch(error => {
            console.error('Error loading animals:', error);
            notify('Ошибка загрузки животных', { type: 'error' });
        }).finally(() => {
            setAnimalsLoading(false);
        });
    }, [dataProvider, notify, record]);

    const handleSave = async () => {
        const animalIds = selectedAnimals.map(a => a.id);

        setSaving(true);
        try {
            await dataProvider.update('routes', {
                id: record.id,
                data: {
                    name: routeName,
                    duration: duration || '',
                    distance: distance || '',
                    is_circular: isCircular,
                    finish_description: finishDescription || '',
                    description: description || '',
                    image_url: imageUrl || '',
                    animals_id: animalIds
                },
                previousData: record
            });
            
            notify('Маршрут успешно обновлен', { type: 'success' });
            redirect('list', 'routes');
        } catch (error) {
            console.error('Error updating route:', error);
            notify(`Ошибка при сохранении: ${error.message}`, { type: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const uploadCroppedRouteImage = async (file) => {
        if (!record || !record.id) {
            notify('Ошибка: ID маршрута не найден', { type: 'error' });
            return;
        }

        setUploading(true);

        try {
            const url = await uploadRouteImage(file, record.id);
            const animalIds = selectedAnimals.length > 0
                ? selectedAnimals.map(a => a.id)
                : (record.animals_id || []);
            setRouteImageFile(file);
            setImageUrl(url);
            setImagePreviewFailed(false);

            await dataProvider.update('routes', {
                id: record.id,
                data: {
                    name: routeName,
                    duration: duration || '',
                    distance: distance || '',
                    is_circular: isCircular,
                    finish_description: finishDescription || '',
                    description: description || '',
                    image_url: url,
                    animals_id: animalIds
                },
                previousData: record
            });

            notify('Картинка маршрута успешно загружена', { type: 'success' });
        } catch (error) {
            console.error('Error uploading route image:', error);
            notify(`Ошибка загрузки: ${error.message}`, { type: 'error' });
        } finally {
            setUploading(false);
        }
    };

    const handleRouteImageChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setCropSourceFile(file);
        setCropDialogOpen(true);
        e.target.value = '';
    };

    const handleCropCancel = () => {
        setCropDialogOpen(false);
        setCropSourceFile(null);
    };

    const handleCropApply = async (croppedFile, error = null) => {
        if (error) {
            notify(`Ошибка обрезки: ${error.message}`, { type: 'error' });
            handleCropCancel();
            return;
        }
        if (croppedFile) {
            await uploadCroppedRouteImage(croppedFile);
        }
        handleCropCancel();
    };

    const handleDelete = async () => {
        setDeleting(true);
        try {
            await dataProvider.delete('routes', {
                id: record.id,
                previousData: record
            });
            
            notify('Маршрут успешно удален', { type: 'success' });
            redirect('list', 'routes');
        } catch (error) {
            console.error('Error deleting route:', error);
            notify(`Ошибка при удалении: ${error.message}`, { type: 'error' });
        } finally {
            setDeleting(false);
            setDeleteDialogOpen(false);
        }
    };
    
    if (!record) {
        return <DetailLoadingState title="Загружаем информацию о маршруте..." />;
    }
    
    const saveDisabled = !routeName.trim() || selectedAnimals.length === 0 || uploading || saving || deleting;
    const hasValidImage = isValidRouteImage(imageUrl) && !imagePreviewFailed;

    return (
        <Box className="route-editor-page">
            <Box className="route-editor-header">
                <Button
                    className="route-editor-back"
                    startIcon={<ArrowBackIcon />}
                    onClick={() => redirect('list', 'routes')}
                >
                    Маршруты
                </Button>
                <Box className="route-editor-heading">
                    <Box className="route-editor-title-icon"><EditNoteOutlinedIcon /></Box>
                    <Box>
                        <Box className="route-editor-title-row">
                            <Typography component="h1">{routeName || 'Маршрут без названия'}</Typography>
                            <Chip size="small" label={`ID ${record.id}`} />
                            {isCircular && <Chip size="small" icon={<LoopIcon />} label="Кольцевой" color="success" />}
                        </Box>
                        <Typography color="text.secondary">
                            Настройте описание, обложку и порядок точек маршрута
                        </Typography>
                    </Box>
                </Box>
            </Box>

            <Box className="route-editor-grid">
                <Box className="route-editor-main-cell">
                    <Box className="route-editor-main-column">
                        <Paper className="route-editor-section">
                            <Box className="route-editor-section-heading">
                                <Box className="route-editor-section-icon"><RouteOutlinedIcon /></Box>
                                <Box>
                                    <Typography component="h2">Основная информация</Typography>
                                    <Typography>То, что посетитель увидит в карточке и на странице маршрута</Typography>
                                </Box>
                            </Box>

                            <Box className="route-editor-fields">
                                <MuiTextField
                                    label="Название маршрута"
                                    value={routeName}
                                    onChange={(e) => setRouteName(e.target.value)}
                                    fullWidth
                                    required
                                    error={!routeName.trim()}
                                    helperText={!routeName.trim() ? 'Введите название маршрута' : 'Короткое и понятное название для посетителей'}
                                    inputProps={{ maxLength: 120 }}
                                />

                                <MuiTextField
                                    label="Время прохождения"
                                    value={duration}
                                    onChange={(e) => setDuration(e.target.value)}
                                    fullWidth
                                    placeholder="Например: 40 минут"
                                    helperText="Укажите время так, как оно должно отображаться в приложении"
                                    InputProps={{
                                        startAdornment: <InputAdornment position="start"><AccessTimeOutlinedIcon fontSize="small" /></InputAdornment>,
                                    }}
                                />

                                <MuiTextField
                                    label="Пройденное расстояние"
                                    value={distance}
                                    onChange={(e) => setDistance(e.target.value)}
                                    fullWidth
                                    placeholder="Например: 1,6 км"
                                    helperText="Расстояние по дорожкам зоопарка"
                                    InputProps={{
                                        startAdornment: <InputAdornment position="start"><MapOutlinedIcon fontSize="small" /></InputAdornment>,
                                    }}
                                />

                                <FormControlLabel
                                    control={<Switch checked={isCircular} onChange={(e) => setIsCircular(e.target.checked)} />}
                                    label="Кольцевой маршрут — возвращает посетителя в зону входа"
                                />

                                {isCircular && (
                                    <MuiTextField
                                        label="Где заканчивается маршрут"
                                        value={finishDescription}
                                        onChange={(e) => setFinishDescription(e.target.value)}
                                        fullWidth
                                        placeholder="Например: у пингвинов, около 80 м до главного входа"
                                        helperText="Эта подсказка отображается в приложении"
                                    />
                                )}

                                <MuiTextField
                                    label="Описание"
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    fullWidth
                                    multiline
                                    minRows={5}
                                    placeholder="Расскажите, чем интересен этот маршрут и кому он подойдёт"
                                    helperText={`${description.length} символов · можно оставить пустым`}
                                />
                            </Box>
                        </Paper>

                        <Paper className="route-editor-section route-editor-animals-section">
                            <Box className="route-editor-section-heading route-editor-section-heading-count">
                                <Box className="route-editor-section-icon"><PetsOutlinedIcon /></Box>
                                <Box>
                                    <Typography component="h2">Животные в маршруте</Typography>
                                    <Typography>Добавьте точки, которые посетитель встретит по пути</Typography>
                                </Box>
                                <Chip
                                    className="route-editor-count-chip"
                                    size="small"
                                    label={`${selectedAnimals.length} выбрано`}
                                />
                            </Box>

                            <Autocomplete
                                multiple
                                className="route-editor-animal-picker"
                                options={animals}
                                loading={animalsLoading}
                                loadingText="Загружаем животных…"
                                noOptionsText="Ничего не найдено"
                                getOptionLabel={(option) => option.name || `Животное #${option.id}`}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                value={selectedAnimals}
                                onChange={(event, newValue) => setSelectedAnimals(newValue)}
                                renderInput={(params) => (
                                    <MuiTextField
                                        {...params}
                                        label="Добавить животное"
                                        placeholder={selectedAnimals.length ? 'Найти ещё' : 'Начните вводить название или ID'}
                                        required
                                        error={!animalsLoading && selectedAnimals.length === 0}
                                        helperText={!animalsLoading && selectedAnimals.length === 0
                                            ? 'Добавьте хотя бы одно животное'
                                            : 'Поиск работает по названию; выбранные животные показаны ниже'}
                                    />
                                )}
                                renderOption={(props, option) => (
                                    <li {...props} key={option.id}>
                                        <Box className="route-editor-animal-option">
                                            <Box className="route-editor-animal-avatar"><PetsOutlinedIcon /></Box>
                                            <Box>
                                                <Typography>{option.name || 'Без названия'}</Typography>
                                                <Typography>Животное ID {option.id}</Typography>
                                            </Box>
                                        </Box>
                                    </li>
                                )}
                                renderTags={(value, getTagProps) =>
                                    value.map((option, index) => (
                                        <Chip
                                            label={option.name || `Животное #${option.id}`}
                                            {...getTagProps({ index })}
                                            key={option.id}
                                            avatar={<Box component="span" className="route-editor-tag-number">{index + 1}</Box>}
                                        />
                                    ))
                                }
                            />
                        </Paper>

                        <Paper className="route-editor-section route-editor-map-section">
                            <Box className="route-editor-section-heading route-editor-map-heading">
                                <Box className="route-editor-section-icon"><MapOutlinedIcon /></Box>
                                <Box>
                                    <Typography component="h2">Маршрут на карте</Typography>
                                    <Typography>Линия строится по координатам животных в порядке выбранных точек</Typography>
                                </Box>
                                <Chip
                                    className="route-editor-map-status"
                                    size="small"
                                    label="Обновляется автоматически"
                                />
                            </Box>
                            <RouteMapPreview animals={selectedAnimals} isCircular={isCircular} />
                        </Paper>
                    </Box>
                </Box>

                <Box className="route-editor-side-cell">
                    <Box className="route-editor-side-column">
                        <Paper className="route-editor-section route-editor-cover-section">
                            <Box className="route-editor-section-heading">
                                <Box className="route-editor-section-icon"><ImageOutlinedIcon /></Box>
                                <Box>
                                    <Typography component="h2">Обложка</Typography>
                                    <Typography>Формат карточки — 361 × 145</Typography>
                                </Box>
                            </Box>

                            <Box className={`route-editor-cover-preview${hasValidImage ? ' has-image' : ''}`}>
                                {hasValidImage ? (
                                    <img
                                        src={imageUrl}
                                        alt={`Обложка маршрута «${routeName || record.name || ''}»`}
                                        onError={() => setImagePreviewFailed(true)}
                                    />
                                ) : (
                                    <Box className="route-editor-cover-placeholder">
                                        <ImageOutlinedIcon />
                                        <Typography>{imageUrl ? 'Не удалось загрузить обложку' : 'Обложка не добавлена'}</Typography>
                                        <Typography>Загрузите изображение в пропорции карточки</Typography>
                                    </Box>
                                )}
                                {uploading && (
                                    <Box className="route-editor-upload-overlay" role="status">
                                        <Box className="route-editor-upload-spinner" />
                                        <Typography>Загружаем обложку…</Typography>
                                    </Box>
                                )}
                            </Box>

                            <Button
                                variant="outlined"
                                component="label"
                                fullWidth
                                className="route-editor-upload-button"
                                startIcon={<CloudUploadOutlinedIcon />}
                                disabled={uploading || saving || deleting}
                            >
                                {hasValidImage ? 'Заменить обложку' : 'Загрузить обложку'}
                                <input
                                    type="file"
                                    hidden
                                    accept="image/*"
                                    onChange={handleRouteImageChange}
                                    disabled={uploading}
                                />
                            </Button>
                            <Typography className="route-editor-upload-help">
                                После выбора откроется кадрирование. Изображение сохраняется сразу.
                            </Typography>
                            {routeImageFile && (
                                <Typography className="route-editor-file-name">
                                    Загружен файл: {routeImageFile.name}
                                </Typography>
                            )}
                        </Paper>

                        <Paper className="route-editor-summary">
                            <Typography component="h2">Перед сохранением</Typography>
                            <Box className="route-editor-summary-row">
                                <Typography>Название</Typography>
                                <Typography className={routeName.trim() ? 'is-ready' : 'is-missing'}>
                                    {routeName.trim() ? 'Готово' : 'Не заполнено'}
                                </Typography>
                            </Box>
                            <Box className="route-editor-summary-row">
                                <Typography>Животные</Typography>
                                <Typography className={selectedAnimals.length ? 'is-ready' : 'is-missing'}>
                                    {selectedAnimals.length ? `${selectedAnimals.length} выбрано` : 'Не выбраны'}
                                </Typography>
                            </Box>
                            <Box className="route-editor-summary-row">
                                <Typography>Обложка</Typography>
                                <Typography className={hasValidImage ? 'is-ready' : ''}>
                                    {hasValidImage ? 'Добавлена' : 'Необязательно'}
                                </Typography>
                            </Box>
                            <Box className="route-editor-summary-row">
                                <Typography>Тип</Typography>
                                <Typography className={isCircular ? 'is-ready' : ''}>
                                    {isCircular ? 'Кольцевой' : 'Линейный'}
                                </Typography>
                            </Box>

                            <Button
                                variant="contained"
                                fullWidth
                                className="route-editor-save-button"
                                startIcon={<SaveOutlinedIcon />}
                                onClick={handleSave}
                                disabled={saveDisabled}
                            >
                                {saving ? 'Сохраняем…' : 'Сохранить маршрут'}
                            </Button>
                            {saveDisabled && !uploading && !saving && !deleting && (
                                <Typography className="route-editor-save-help">
                                    Заполните название и добавьте хотя бы одно животное
                                </Typography>
                            )}
                        </Paper>

                        <Button
                            color="error"
                            className="route-editor-delete-button"
                            startIcon={<DeleteOutlineIcon />}
                            onClick={() => setDeleteDialogOpen(true)}
                            disabled={uploading || saving || deleting}
                        >
                            Удалить маршрут
                        </Button>
                    </Box>
                </Box>

                <ImageCropDialog
                    open={cropDialogOpen}
                    file={cropSourceFile}
                    title="Обрезка картинки маршрута"
                    helperText="Размер контейнера в приложении: 361x145. Итог загружается с запасом качества."
                    aspectRatio={ROUTE_CARD_ASPECT}
                    outputWidth={ROUTE_EXPORT_WIDTH}
                    outputHeight={ROUTE_EXPORT_HEIGHT}
                    onCancel={handleCropCancel}
                    onApply={handleCropApply}
                />

                <Dialog
                    open={deleteDialogOpen}
                    onClose={() => !deleting && setDeleteDialogOpen(false)}
                    className="route-editor-delete-dialog"
                    aria-labelledby="route-delete-title"
                >
                    <DialogTitle id="route-delete-title">Удалить маршрут?</DialogTitle>
                    <DialogContent>
                        <Typography>
                            Маршрут «{routeName || record.name || `ID ${record.id}`}» исчезнет из приложения. Это действие нельзя отменить.
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>Отмена</Button>
                        <Button
                            color="error"
                            variant="contained"
                            onClick={handleDelete}
                            disabled={deleting}
                            startIcon={<DeleteOutlineIcon />}
                        >
                            {deleting ? 'Удаляем…' : 'Удалить маршрут'}
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </Box>
    );
};

// Компонент редактирования маршрута
export const RouteEdit = () => {
    return (
        <Edit title="Редактирование маршрута" actions={false} className="route-editor-edit">
            <SimpleForm
                toolbar={false}
                sx={{
                    maxWidth: 'none',
                    width: '100%',
                    '& .RaSimpleForm-form': {
                        maxWidth: 'none',
                        width: '100%',
                    },
                    '& .RaSimpleForm-stack': {
                        maxWidth: 'none',
                        width: '100%',
                        alignItems: 'stretch',
                    },
                }}
            >
                <RouteEditContent />
            </SimpleForm>
        </Edit>
    );
};
